import { ADMIN_API_PREFIX, fillPath, route } from "./routes.js";
import { z } from "zod";
import { createDcrRedirectHostSchema, dcrRedirectHostSummarySchema, updateDcrRedirectHostSchema } from "@zion/sdk-contracts";
//#region src/dcr-redirect-hosts.ts
/**
* `/v1/admin/dcr-redirect-hosts` — the per-org allowlist of hosts a self-registering (RFC 7591)
* OAuth client may name in its `redirect_uris`.
*
* Producer `DcrRedirectHostsController` in core-api, which lives under `src/oauth/`, not
* `src/admin/`. Every shape already lives in `@zion/sdk-contracts` (`oauth.ts`), so this file
* declares routes, one path builder and the request and response schemas over those shapes.
*
* Permission `settings:manage` on the class, plus `NoDelegatedPrincipalGuard`: a delegated token
* cannot add its own host to the allowlist that bounds it. The host grammar is policy, not shape,
* and lives in core-api's `redirect-host-allowlist.ts`; the schemas here bound shape only.
*
* Two facts the shell's `api/client.ts` gets wrong, and the controller is the fact:
* `deleteDcrRedirectHost` is typed `Promise<void>` while the controller answers 200 with
* `{ message: string }`; and `:id` is a uuid (`dcrRedirectHostIdParamSchema`), which the shell
* interpolates without validating.
*
* Direction rule (R-28): every response schema here is `z.object`, so a member core-api adds is
* stripped rather than refused and one new column cannot red every page that reads the route.
* Every request schema is `z.strictObject`, so a key the server would reject is refused before
* the request leaves the client.
* No shape in this file travels in both directions.
* The three entry responses read {@link dcrRedirectHostSummaryViewSchema}, a
* `z.object(dcrRedirectHostSummarySchema.shape)` rebuild of the `@zion/sdk-contracts` shape
* (R-38). That package stays the shape's owner; a test pins the two key sets equal so the view
* cannot drift from it.
*/
/** The collection path. No parameter, so the OpenAPI form and the request form are the same. */
const ADMIN_DCR_REDIRECT_HOSTS_PATH = `${ADMIN_API_PREFIX}/dcr-redirect-hosts`;
/** One entry, in OpenAPI `{param}` form. Fill it with {@link adminDcrRedirectHostPath}. */
const ADMIN_DCR_REDIRECT_HOST_PATH_TEMPLATE = `${ADMIN_DCR_REDIRECT_HOSTS_PATH}/{id}`;
/**
* `GET /v1/admin/dcr-redirect-hosts`. Every entry for the caller's org, enabled or not — a
* disabled entry stays visible, because it is the trail of what an admin approved. Permission
* `settings:manage`.
*/
const LIST_DCR_REDIRECT_HOSTS_ROUTE = route("GET", ADMIN_DCR_REDIRECT_HOSTS_PATH);
/**
* `POST /v1/admin/dcr-redirect-hosts`. Adds one host; 201 with the created entry, 400 when the
* host violates the allowlist grammar, 409 when this org already registered it. Permission
* `settings:manage`.
*/
const CREATE_DCR_REDIRECT_HOST_ROUTE = route("POST", ADMIN_DCR_REDIRECT_HOSTS_PATH);
/**
* `PATCH /v1/admin/dcr-redirect-hosts/{id}`. Edits the description or the enabled flag; 200 with
* the updated entry, 404 for an unknown or cross-org entry. `host` is immutable and absent from
* the body. Permission `settings:manage`.
*/
const UPDATE_DCR_REDIRECT_HOST_ROUTE = route("PATCH", ADMIN_DCR_REDIRECT_HOST_PATH_TEMPLATE);
/**
* `DELETE /v1/admin/dcr-redirect-hosts/{id}`. Permanent, and it discards the entry's provenance.
* Answers 200 with a message body, never 204. Permission `settings:manage`.
*/
const DELETE_DCR_REDIRECT_HOST_ROUTE = route("DELETE", ADMIN_DCR_REDIRECT_HOST_PATH_TEMPLATE);
/**
* Fill {@link ADMIN_DCR_REDIRECT_HOST_PATH_TEMPLATE} with one entry id.
*
* @param id the allowlist entry's uuid, as `dcrRedirectHostSummarySchema.id` publishes it. Throws
*   when it is empty, longer than 128 characters, or carries a character outside the path-param
*   grammar.
* @returns the request path, each segment percent-encoded.
*/
function adminDcrRedirectHostPath(id) {
	return fillPath(ADMIN_DCR_REDIRECT_HOST_PATH_TEMPLATE, { id });
}
/**
* The allowlist entry every route here returns, relaxed to strip an unknown member (R-38).
*
* `dcrRedirectHostSummarySchema` in `@zion/sdk-contracts` (`oauth.ts`) owns this shape and stays
* its home. This is `z.object(upstream.shape)`: the same members, the strictness dropped, so a
* member core-api adds cannot red every page that reads the route. The upstream carries no
* `.refine()`, so the rebuild loses nothing else, and `dcr-redirect-hosts.test.ts` pins the two
* key sets equal.
*
* `host` is a bare string on purpose, upstream's choice and kept: the write door plus the grammar
* validator are the gate, and the read path renders stored reality.
*/
const dcrRedirectHostSummaryViewSchema = z.object(dcrRedirectHostSummarySchema.shape);
/** `GET /v1/admin/dcr-redirect-hosts` response: every entry, unpaged. */
const listDcrRedirectHostsResponseSchema = z.array(dcrRedirectHostSummaryViewSchema);
/**
* `POST /v1/admin/dcr-redirect-hosts` body. Owned by `@zion/sdk-contracts`
* (`createDcrRedirectHostSchema`): a bare host (`claude.ai`, `*.claude.ai`) or a scheme-bound
* host (`cursor://anysphere.cursor-mcp`), never a full URI.
*/
const createDcrRedirectHostRequestSchema = createDcrRedirectHostSchema;
/** `POST /v1/admin/dcr-redirect-hosts` response: the created entry. */
const createDcrRedirectHostResponseSchema = dcrRedirectHostSummaryViewSchema;
/**
* `PATCH /v1/admin/dcr-redirect-hosts/{id}` body. Owned by `@zion/sdk-contracts`
* (`updateDcrRedirectHostSchema`): `host` is absent by construction, so an attempt to edit it in
* place is a 400 rather than a silent rewrite of who approved what.
*/
const updateDcrRedirectHostRequestSchema = updateDcrRedirectHostSchema;
/** `PATCH /v1/admin/dcr-redirect-hosts/{id}` response: the updated entry. */
const updateDcrRedirectHostResponseSchema = dcrRedirectHostSummaryViewSchema;
/**
* `DELETE /v1/admin/dcr-redirect-hosts/{id}` response.
*
* The route answers 200 with this body. `DcrRedirectHostsService.remove` returns `{ message }`
* and the controller passes it through.
*/
const deleteDcrRedirectHostResponseSchema = z.object({ 
/** Human-readable confirmation from the service. Not a machine-readable code. */
message: z.string() });
/** Every route this file declares, for the package's route census. */
const dcrRedirectHostsRoutes = Object.freeze([
	LIST_DCR_REDIRECT_HOSTS_ROUTE,
	CREATE_DCR_REDIRECT_HOST_ROUTE,
	UPDATE_DCR_REDIRECT_HOST_ROUTE,
	DELETE_DCR_REDIRECT_HOST_ROUTE
]);
//#endregion
export { ADMIN_DCR_REDIRECT_HOSTS_PATH, ADMIN_DCR_REDIRECT_HOST_PATH_TEMPLATE, CREATE_DCR_REDIRECT_HOST_ROUTE, DELETE_DCR_REDIRECT_HOST_ROUTE, LIST_DCR_REDIRECT_HOSTS_ROUTE, UPDATE_DCR_REDIRECT_HOST_ROUTE, adminDcrRedirectHostPath, createDcrRedirectHostRequestSchema, createDcrRedirectHostResponseSchema, dcrRedirectHostSummaryViewSchema, dcrRedirectHostsRoutes, deleteDcrRedirectHostResponseSchema, listDcrRedirectHostsResponseSchema, updateDcrRedirectHostRequestSchema, updateDcrRedirectHostResponseSchema };

//# sourceMappingURL=dcr-redirect-hosts.js.map